import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Plus, X, Filter, Wrench, Wifi, Droplet, Zap, Sparkles, AlertTriangle, Clock, CheckCircle2, Archive } from "lucide-react";

const COLUMNS = [
  { v: "open", l: "Open", c: "amber", icon: AlertTriangle },
  { v: "in_progress", l: "In Progress", c: "blue", icon: Clock },
  { v: "resolved", l: "Resolved", c: "emerald", icon: CheckCircle2 },
  { v: "closed", l: "Closed", c: "gray", icon: Archive },
];

const CATEGORIES = [
  { v: "plumbing", l: "Plumbing", i: Droplet },
  { v: "electrical", l: "Electrical", i: Zap },
  { v: "wifi", l: "Wifi", i: Wifi },
  { v: "cleaning", l: "Cleaning", i: Sparkles },
  { v: "general", l: "General", i: Wrench },
];

const PRIORITIES = [
  { v: "low", l: "Low", c: "bg-gray-100 text-gray-700 border-gray-200" },
  { v: "medium", l: "Medium", c: "bg-amber-100 text-amber-700 border-amber-200" },
  { v: "high", l: "High", c: "bg-rose-100 text-rose-700 border-rose-200" },
  { v: "urgent", l: "Urgent", c: "bg-red-600 text-white border-red-700" },
];

export default function Maintenance() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [properties, setProperties] = useState([]);
  const [filter, setFilter] = useState("all");

  const [form, setForm] = useState({
    property_id: "", category: "general", priority: "medium", title: "", description: "",
  });

  useEffect(() => {
    if (loading) return;
    if (!user || typeof user !== "object") { nav("/login", { state: { from: "/maintenance" }}); return; }
    refresh();
    // For student: load their bookings to populate property list. For owner/admin: load own properties.
    if (user.role === "owner" || user.role === "admin" || user.role === "super_admin") {
      api.get("/properties", { params: { owner_id: user.id } }).then(r => setProperties(r.data || []));
    } else {
      // student — list all properties (could be filtered to their booked ones in v2)
      api.get("/properties", { params: { limit: 50 } }).then(r => setProperties(r.data || []));
    }
    // eslint-disable-next-line
  }, [user, loading]);

  const refresh = async () => {
    try { const { data } = await api.get("/maintenance/tickets"); setTickets(data || []); } catch { /* */ }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.property_id || !form.title.trim()) return toast.error("Property + title required");
    try {
      await api.post("/maintenance/tickets", form);
      toast.success("Ticket raised");
      setShowNew(false);
      setForm({ property_id: "", category: "general", priority: "medium", title: "", description: "" });
      refresh();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const moveStatus = async (tid, newStatus) => {
    try {
      await api.patch(`/maintenance/tickets/${tid}`, { status: newStatus });
      toast.success(`Moved to ${newStatus.replace("_", " ")}`);
      refresh();
    } catch { toast.error("Couldn't update"); }
  };

  const filtered = tickets.filter(t => filter === "all" || t.priority === filter);
  const byColumn = (s) => filtered.filter(t => t.status === s);
  const canEdit = user && (user.role === "owner" || user.role === "admin" || user.role === "super_admin");

  if (loading) return null;

  return (
    <div className="bg-[#FBFAF6] min-h-screen" data-testid="maintenance-page">
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-8">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-[#22B36A]">Maintenance Board</div>
            <h1 className="font-display font-black text-3xl md:text-4xl mt-1 tracking-tight text-[#111111]">Tickets & Repairs</h1>
            <p className="text-gray-500 text-sm mt-1">Track every complaint from raise to close — drag-style Kanban.</p>
          </div>
          <div className="flex gap-2">
            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2">
              <Filter size={14} className="text-gray-400"/>
              <select value={filter} onChange={e=>setFilter(e.target.value)} className="text-sm font-semibold outline-none bg-transparent" data-testid="priority-filter">
                <option value="all">All priorities</option>
                {PRIORITIES.map(p => <option key={p.v} value={p.v}>{p.l}</option>)}
              </select>
            </div>
            <button onClick={()=>setShowNew(true)} className="inline-flex items-center gap-1.5 bg-[#34C97B] hover:bg-[#22B36A] text-white px-4 py-2 rounded-xl text-sm font-semibold transition" data-testid="new-ticket-btn">
              <Plus size={14}/> Raise ticket
            </button>
          </div>
        </div>

        {/* Kanban */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kanban-board">
          {COLUMNS.map(col => (
            <div key={col.v} className="bg-gray-50 rounded-2xl p-3 min-h-[400px]" data-testid={`column-${col.v}`}>
              <div className="flex items-center justify-between mb-3 px-1">
                <div className="flex items-center gap-2">
                  <col.icon size={14} className={`text-${col.c}-600`}/>
                  <h3 className="font-display font-black text-sm uppercase tracking-wider text-gray-700">{col.l}</h3>
                </div>
                <span className="text-xs font-bold bg-white border border-gray-200 rounded-full px-2 py-0.5">{byColumn(col.v).length}</span>
              </div>
              <div className="space-y-2">
                {byColumn(col.v).map(t => {
                  const cat = CATEGORIES.find(c => c.v === t.category) || CATEGORIES[4];
                  const pri = PRIORITIES.find(p => p.v === t.priority) || PRIORITIES[1];
                  return (
                    <div key={t.id} className="bg-white rounded-xl border border-gray-200 p-3 hover:shadow-md transition" data-testid={`ticket-${t.id}`}>
                      <div className="flex items-center gap-2 mb-1.5">
                        <cat.i size={12} className="text-gray-500"/>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">{cat.l}</span>
                        <span className={`ml-auto text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full border ${pri.c}`}>{pri.l}</span>
                      </div>
                      <h4 className="font-bold text-sm text-[#111111] leading-tight">{t.title}</h4>
                      <div className="text-xs text-gray-500 mt-1 line-clamp-2">{t.property_title}</div>
                      {t.description && <p className="text-xs text-gray-600 mt-2 line-clamp-2">{t.description}</p>}
                      <div className="text-[10px] text-gray-400 mt-2">By {t.raised_by_name} · {new Date(t.created_at).toLocaleDateString()}</div>
                      {canEdit && (
                        <div className="mt-2 flex gap-1 pt-2 border-t border-gray-100">
                          {COLUMNS.filter(c => c.v !== t.status).map(c => (
                            <button key={c.v} onClick={()=>moveStatus(t.id, c.v)} className="flex-1 text-[10px] font-bold uppercase tracking-wider py-1 rounded bg-gray-50 hover:bg-[#E8FFF3] hover:text-[#22B36A] transition" data-testid={`move-${t.id}-${c.v}`}>→ {c.l}</button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
                {byColumn(col.v).length === 0 && (
                  <div className="text-center text-xs text-gray-400 py-8">No tickets</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* New ticket modal */}
      {showNew && (
        <div className="fixed inset-0 z-[70] bg-black/40 backdrop-blur-sm grid place-items-center p-5" onClick={()=>setShowNew(false)} data-testid="new-ticket-modal">
          <div className="bg-white rounded-3xl border border-gray-200 p-6 md:p-8 max-w-lg w-full" onClick={e=>e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display font-black text-2xl text-[#111111]">Raise a ticket</h3>
              <button onClick={()=>setShowNew(false)} className="p-2 rounded-full hover:bg-gray-100"><X size={18}/></button>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500 block mb-1.5">Property</label>
                <select required value={form.property_id} onChange={e=>setForm({...form, property_id: e.target.value})} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 outline-none focus:border-[#34C97B] focus:ring-2 focus:ring-[#E8FFF3] text-sm" data-testid="ticket-property">
                  <option value="">Select property...</option>
                  {properties.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-500 block mb-1.5">Category</label>
                  <div className="flex flex-wrap gap-1.5">
                    {CATEGORIES.map(c => (
                      <button type="button" key={c.v} onClick={()=>setForm({...form, category: c.v})}
                        className={`px-2.5 py-1 rounded-full text-xs font-bold border transition ${form.category === c.v ? "bg-[#34C97B] text-white border-[#34C97B]" : "bg-white text-gray-600 border-gray-200"}`}>{c.l}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-500 block mb-1.5">Priority</label>
                  <div className="flex flex-wrap gap-1.5">
                    {PRIORITIES.map(p => (
                      <button type="button" key={p.v} onClick={()=>setForm({...form, priority: p.v})}
                        className={`px-2.5 py-1 rounded-full text-xs font-bold border transition ${form.priority === p.v ? "bg-[#34C97B] text-white border-[#34C97B]" : "bg-white text-gray-600 border-gray-200"}`}>{p.l}</button>
                    ))}
                  </div>
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500 block mb-1.5">Title</label>
                <input required value={form.title} onChange={e=>setForm({...form, title: e.target.value})} placeholder="Wifi down in block A" className="w-full px-4 py-2.5 rounded-xl border border-gray-200 outline-none focus:border-[#34C97B] focus:ring-2 focus:ring-[#E8FFF3] text-sm" data-testid="ticket-title"/>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500 block mb-1.5">Description (optional)</label>
                <textarea value={form.description} onChange={e=>setForm({...form, description: e.target.value})} placeholder="Since 2 days, router blinking red..." className="w-full px-4 py-2.5 rounded-xl border border-gray-200 outline-none focus:border-[#34C97B] focus:ring-2 focus:ring-[#E8FFF3] text-sm min-h-[80px]"/>
              </div>
              <button className="w-full bg-[#34C97B] hover:bg-[#22B36A] text-white font-semibold py-3 rounded-xl transition" data-testid="ticket-submit">Submit ticket</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
